import { useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"
import React from "react"
import {
	isReactSelectSingleOption,
	TOnChangeReactSelect,
	TOnChangeReactSelectField,
	TReactSelectOption,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import { useTranslation } from "react-i18next"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import {
	IJamaItem,
	IJamaProjectsAndFolders,
	IMainField,
} from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import createReactSelectOptions from "../ToolFieldProperties/createReactSelectOptions"
import FormField from "../FormField"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { IItemType, IUserIdField } from "../../../../Interfaces/FormsInterface"
import { REACT_SELECT_FAIL_FALLBACK_OPTIONS } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import { defaultItemTypeFormData } from "components/ProjectSpecificComponents/Stores/FormStore/defaults"
import parseDataForReactSelect from "components/ProjectSpecificComponents/Functions/parseDataForReactSelect"

interface Props {}

export const ItemForm = observer(({}: Props) => {
	const store = useStore()
	const { isMobile } = useScreenType()
	const formStore = useFormStore()
	const { t } = useTranslation()
	const [allToolItems, setAllToolItems] = useState<TReactSelectOption[]>([])
	const { instanceAnonymousUsers } = store
	const [
		itemTypeId,
		userIdFieldId,
		userEmailFieldId,
		userMobFieldId,
		userDepFieldId,
		userNameFieldId,
	] = useMemo(() => {
		if (formStore.selectedFormsConfig == null) return []

		let {
			itemType: { id: itemTypeId },
			userIdField: { fieldId: userIdFieldId },
			userEmailField: { fieldId: userEmailFieldId },
			userMobField: { fieldId: userMobFieldId },
			userDepField: { fieldId: userDepFieldId },
			userNameField: { fieldId: userNameFieldId },
		} = formStore.selectedFormsConfig

		return [
			itemTypeId,
			userIdFieldId,
			userEmailFieldId,
			userMobFieldId,
			userDepFieldId,
			userNameFieldId,
		]
	}, [formStore.selectedFormsConfig])

	useEffect(() => {
		const getAllItems = async () => {
			const data = await formStore.getAllJamaItems(true)
			setAllToolItems(parseDataForReactSelect(data))
		}
		getAllItems()
	}, [])

	useEffect(() => {
		if (itemTypeId == null) return
		store.setSelectedToolId(itemTypeId)
	}, [itemTypeId])

	useEffect(() => {
		const getAndSetRequestItemFields = async (itemId: string) => {
			const { ok, data } = await AppRequests("ItemType").get<IMainField[]>("fields", {
				requestedItemId: itemId,
			})
			if (ok && data) {
				formStore.setMainFieldsData(data)
			} else {
				//if api fails then deletes old values
				formStore.setMainFieldsData([])
			}
		}
		if (store.selectedToolId) {
			getAndSetRequestItemFields(store.selectedToolId)
			getAndSetProjectAndFoldersData(store.selectedToolId)
		}
	}, [store.selectedToolId])

	/**
	 *  Sets all available project and project folders in store
	 */
	const getAndSetProjectAndFoldersData = async (itemId: string) => {
		store.setLoaderOverlayOpen(true)
		const { ok, data } = await AppRequests("Collection").get<IJamaProjectsAndFolders[]>(
			"allProjectsWithFolders",
			{
				requestedItemId: itemId,
			}
		)
		if (ok && data) {
			formStore.setProjectsAndFoldersData(data)
		}
		store.setLoaderOverlayOpen(false)
	}

	// Only string and writable fields are allowed as user id field
	const usableMainFieldOptions =
		formStore.mainFieldsData?.filter(
			({ fieldType, readOnly }) => fieldType === "STRING" && !readOnly
		) ?? []

	const mainFieldOptions = useMemo(
		() => createReactSelectOptions(usableMainFieldOptions ?? [], "fieldId", "name"),
		[formStore.mainFieldsData]
	)

	const fieldOptions: TReactSelectOption[] = useMemo(
		() =>
			mainFieldOptions.filter((item) => {
				if (formStore.selectedFormsConfig == null) return REACT_SELECT_FAIL_FALLBACK_OPTIONS
				// If field already used then option disabled
				if (
					item.value === userIdFieldId ||
					item.value === userEmailFieldId ||
					item.value === userMobFieldId ||
					item.value === userDepFieldId ||
					item.value === userNameFieldId
				) {
					item.isDisabled = true
				} else {
					item.isDisabled = false
				}
				return item
			}),
		[formStore.selectedFormsConfig, mainFieldOptions, formStore.mainFieldsData]
	)

	// Responsible for calling the function
	// that automatically generates the required fields into forms
	useEffect(() => {
		if (formStore.mainFieldsData == null) return
		formStore.ensureRequiredFormFieldsExist(formStore.mainFieldsData)
	}, [formStore.mainFieldsData])

	const setItemTypeSettings: TOnChangeReactSelect = (selectedOption) => {
		const data: IItemType = isReactSelectSingleOption(selectedOption)
			? {
					id: selectedOption.value,
					name: selectedOption.label,
			  }
			: defaultItemTypeFormData

		formStore.clearAdminPanelPageForms()
		formStore.mergeItemFieldFormData(data, "itemType")
	}

	const setUserIdentifierSettings: TOnChangeReactSelectField = (
		selectedOption,
		triggeredAction,
		fieldType,
		fieldValue = ""
	) => {
		const shouldClearValue = triggeredAction?.action === "clear"
		const data: IUserIdField = {
			fieldId: "",
			name: "",
		}

		if (isReactSelectSingleOption(selectedOption) && !shouldClearValue) {
			const { label, value } = selectedOption
			data.fieldId = value
			data.name = label
		}

		switch (fieldType) {
			case "userID":
				formStore.mergeItemFieldFormData(data, "userIdField")
				break
			case "userEmail":
				formStore.mergeItemFieldFormData(data, "userEmailField")
				break
			case "userMob":
				formStore.mergeItemFieldFormData(data, "userMobField")
				break
			case "userDep":
				formStore.mergeItemFieldFormData(data, "userDepField")
				break
			case "userName":
				formStore.mergeItemFieldFormData(data, "userNameField")
				break
		}
		if (instanceAnonymousUsers) {
			// If different picklist is selected then remove from new request form
			if (isReactSelectSingleOption(selectedOption) && fieldValue !== selectedOption.value) {
				formStore.deleteNewRequestFormField(fieldValue)
			}

			//Adds selected picklist to new request form as field
			formStore.ensurePicklistFieldValueExist(
				isReactSelectSingleOption(selectedOption) ? selectedOption.value : ""
			)
		} else {
			// Deletes selected field from newRequestForm if it exists there
			formStore.deleteNewRequestFormField(data.fieldId)
		}
		// Checks if all mandatory fields are in newRequestForm that are not used in item fields
		if (formStore.mainFieldsData == null) return
		formStore.ensureRequiredFormFieldsExist(formStore.mainFieldsData)
	}

	return (
		<HyperDiv direction={isMobile ? "column" : "row"} padding="2.5rem 0">
			<HyperDiv width={isMobile ? undefined : 250}>
				<FormField
					direction={isMobile ? "Horizontal" : "Vertical"}
					label={`${t(`forms.field-label.item`)} *`}
					placeholderValue=""
					editable
				>
					<Select options={allToolItems} valueKey={itemTypeId} onChange={setItemTypeSettings} />
				</FormField>
			</HyperDiv>
			<Whitespace h x="2.25" />
			{!instanceAnonymousUsers && (
				<>
					<HyperDiv width={isMobile ? undefined : 250}>
						<FormField
							label={`${t(`forms.field-label.user-identifier`)} *`}
							direction={isMobile ? "Horizontal" : "Vertical"}
							placeholderValue=""
							editable
						>
							<Select
								options={fieldOptions}
								valueKey={userIdFieldId}
								onChange={(selectedOption, triggeredAction) =>
									setUserIdentifierSettings(
										selectedOption,
										triggeredAction,
										"userID",
										userIdFieldId
									)
								}
							/>
						</FormField>
					</HyperDiv>
					<Whitespace h x="2.25" />
					<HyperDiv width={isMobile ? undefined : 250}>
						<FormField
							label={`${t(`forms.field-label.user-email-identifier`)} *`}
							direction={isMobile ? "Horizontal" : "Vertical"}
							placeholderValue=""
							editable
						>
							<Select
								options={fieldOptions}
								valueKey={userEmailFieldId}
								onChange={(selectedOption, triggeredAction) =>
									setUserIdentifierSettings(
										selectedOption,
										triggeredAction,
										"userEmail",
										userEmailFieldId
									)
								}
							/>
						</FormField>
					</HyperDiv>
					<Whitespace h x="2.25" />
					<HyperDiv width={isMobile ? undefined : 250}>
						<FormField
							label={t(`forms.field-label.user-phone-identifier`)}
							direction={isMobile ? "Horizontal" : "Vertical"}
							placeholderValue=""
							editable
						>
							<Select
								options={fieldOptions}
								valueKey={userMobFieldId}
								onChange={(selectedOption, triggeredAction) =>
									setUserIdentifierSettings(
										selectedOption,
										triggeredAction,
										"userMob",
										userMobFieldId
									)
								}
							/>
						</FormField>
					</HyperDiv>
					<Whitespace h x="2.25" />
					<HyperDiv width={isMobile ? undefined : 250}>
						<FormField
							label={t(`forms.field-label.user-department-identifier`)}
							direction={isMobile ? "Horizontal" : "Vertical"}
							placeholderValue=""
							editable
						>
							<Select
								options={fieldOptions}
								valueKey={userDepFieldId}
								onChange={(selectedOption, triggeredAction) =>
									setUserIdentifierSettings(
										selectedOption,
										triggeredAction,
										"userDep",
										userDepFieldId
									)
								}
							/>
						</FormField>
					</HyperDiv>
					<Whitespace h x="2.25" />
					<HyperDiv width={isMobile ? undefined : 250}>
						<FormField
							label={t(`forms.field-label.user-name-identifier`)}
							direction={isMobile ? "Horizontal" : "Vertical"}
							placeholderValue=""
							editable
						>
							<Select
								options={fieldOptions}
								valueKey={userNameFieldId}
								onChange={(selectedOption, triggeredAction) => {
									setUserIdentifierSettings(
										selectedOption,
										triggeredAction,
										"userName",
										userNameFieldId
									)
								}}
							/>
						</FormField>
					</HyperDiv>
				</>
			)}
		</HyperDiv>
	)
})

export default ItemForm
